import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";
import {
  BiArrowToRight,
  BiAt,
  BiBrain,
  BiFastForwardCircle,
  BiFile,
  BiLinkExternal,
  BiPaperclip,
  BiShuffle,
  BiSolidBot,
  BiSolidExit,
  BiSolidImageAlt,
  BiSolidMegaphone,
  BiSolidShapes,
  BiSolidTimeFive,
  BiToggleLeft,
  BiUndo,
  BiUserVoice,
  BiVideo,
  BiX
} from "react-icons/bi";
import { FaListAlt } from "react-icons/fa";

import i18n from "../../../i18n/config";
import { MakeIcon } from "../../common/icons/make.icon";
import { SuperUserManagerIcon } from "../../common/icons/superUserManager.icon";
import VariableIcon from "../../common/icons/variable.icon";
import PopoverWithAudio from "../../common/PopoverWithAudio";
import ScenarioYoutubeVideo from "../../common/ScenarioYoutubeVideo";
import { MacBotActionKind, YoutubePosition } from "./types";
import { BotTypeEnum } from "../../../utils/BotTypeEnum";

interface scenarioHeaderProps {
  onClose: () => void;
  type: MacBotActionKind;
  scenarioType?: BotTypeEnum;
}

const ScenarioHeader = ({
  onClose,
  type,
  scenarioType
}: scenarioHeaderProps) => {
  const { t } = useTranslation();
  const isVideo = scenarioType == BotTypeEnum.VIDEO_SCENARIO;
  const HEADER_CONFIG: Record<
    MacBotActionKind,
    {
      title: string;
      tooltip: string;
      youtube: string;
      icon: JSX.Element | undefined;
      audioPath: { [language: string]: string };
      tooltipImage: string;
      description: string;
    }
  > = {
    [MacBotActionKind.ShowGotoForm]: {
      title: ReactHtmlParser(t("MacBotGotoForm/title")),
      tooltip: t("MacBotGotoForm/Tooltips"),
      youtube: "GO_TO",
      icon: <BiUndo className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716212247_go_to_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716212231_go_to_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813145815_Go_to_a_block-min.png",
      description: t("MacBotGotoForm/MacBotGotoForm")
    },
    [MacBotActionKind.ShowAttachmentForm]: {
      title: ReactHtmlParser(t("MacBotAttachmentForm/key1")),
      tooltip: t("MacBotAttachmentForm/Tooltips"),
      youtube: "SEND_ATTACHMENT",
      icon: (
        <BiPaperclip className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716212354_attachement_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716212316_attachement_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125758_Bot_Attachment-min.png",
      description: t("MacBotAttachmentForm/description")
    },
    [MacBotActionKind.ShowButtonForm]: {
      title: ReactHtmlParser(t("MacBotButtonForm/title")),
      tooltip: t("MacBotButtonForm/Tooltips"),
      youtube: "ADD_BUTTON",
      icon: (
        <BiToggleLeft className="tw-w-6 tw-h-6 tw-relative  tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/2409271624276_add_interaction_btn_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242741_add_interaction_btn_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125879_Button-min.png",
      description: isVideo
        ? t("MacBotButtonForm/videoDescription")
        : t("MacBotButtonForm/description")
    },
    [MacBotActionKind.ShowConditionForm]: {
      title: ReactHtmlParser(t("MacBotConditionForm/key1")),
      tooltip: t("MacBotConditionForm/Tooltips"),
      youtube: "CONDITIONS",
      icon: (
        <BiShuffle className="tw-w-6 tw-h-6 tw-relative  tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230392_add_conditions_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230369_add_conditions_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125832_Conditions-min.png",
      description: t("MacBotConditionForm/description")
    },
    [MacBotActionKind.ShowCreateVariableForm]: {
      title: ReactHtmlParser(t("MacBotCreateVariableForm/key1")),
      tooltip: t("MacBotCreateVariableForm/Tooltips"),
      youtube: "SEND_ATTACHMENT",
      icon: <VariableIcon />,
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230298_create_variable_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230397_create_variable_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/2501281312586_Create_Variable-min.png",
      description: t("MacBotCreateVariableForm/description")
    },
    [MacBotActionKind.ShowDelayForm]: {
      title: ReactHtmlParser(t("MacDelayForm/key1")),
      tooltip: t("MacDelayForm/Tooltips"),
      youtube: "DELAY",
      icon: (
        <BiSolidTimeFive className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24101621074811_delay_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/2410162107481_delay_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125733_Add_Delay-min.png",
      description: t("MacDelayForm/description")
    },
    [MacBotActionKind.ShowEndOfConversationForm]: {
      title: ReactHtmlParser(t("MacEndConversation/key1")),
      tooltip: t("MacEndConversation/Tooltips"),
      youtube: "END_OF_CONVERSATION",
      icon: (
        <BiSolidExit className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24101620583949_conversation_end_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24101620583925_conversation_end_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125888_End_of_Conversation-min.png",
      description: t("MacEndConversation/description")
    },
    [MacBotActionKind.ShowHTTPRequestForm]: {
      title: ReactHtmlParser(t("McBotHttpForm/key3")),
      tooltip: t("McBotHttpForm/Tooltips"),
      icon: <BiSolidMegaphone className="tw-w-6 tw-h-6 tw-relative" />,
      youtube: "HTTP_REQUEST",
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230384_http_call_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230463_http_call_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813145844_Make_a_HTTP_Request-min.png",
      description: t("McBotHttpForm/description")
    },
    [MacBotActionKind.ShowMakeWebHookForm]: {
      title: ReactHtmlParser(t("MacMakeCom/key1")),
      tooltip: t("MacMakeCom/Tooltips"),
      youtube: "MAKE_WEBHOOK",
      icon: <MakeIcon />,
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/25021113384659_make_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/25021113384630_make_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813145844_Make_a_HTTP_Request-min.png",
      description: t("AddItemModal/description")
    },
    [MacBotActionKind.ShowLeadForm]: {
      title: ReactHtmlParser(t("MacBotLeadForm/key1")),
      tooltip: t("MacBotLeadForm/Tooltips"),
      youtube: "LEAD_FORM",
      icon: (
        <FaListAlt className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716212227_lead_form_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716212213_lead_form_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813145830_Lead_Form-min.png",
      description: t("MacBotLeadForm/description")
    },
    [MacBotActionKind.ShowMediaForm]: {
      title: t("MacBotMediaForm/key1"),
      tooltip: t("MacBotMediaForm/Tooltips"),
      youtube: "SEND_MEDIA",
      icon: (
        <BiSolidImageAlt className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24101509031673_media_file_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24101509031673_media_file_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125777_Bot_Media-min.png",
      description: t("MacBotMediaForm/description")
    },
    [MacBotActionKind.ShowOverrideVariableForm]: {
      title: ReactHtmlParser(t("MacBotOverrideVariableForm/key1")),
      tooltip: t("MacBotOverrideVariableForm/Tooltips"),
      youtube: "OVERRIDE_VARIABLE",
      icon: (
        <BiArrowToRight className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230312_override_variable_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/2409271623039_override_variable_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813145889_Override_Variable-min.png",
      description: t("MacBotOverrideVariableForm/description")
    },
    [MacBotActionKind.ShowResponseForm]: {
      title: isVideo
        ? ReactHtmlParser(t("VideoMacBotResponseForm/title"))
        : ReactHtmlParser(t("MacBotResponseForm/title")),
      tooltip: t("MacBotResponseForm/Tooltips"),
      youtube: isVideo ? "VIDEOBOT_MESSAGE" : "BOT_MESSAGE",
      icon: isVideo ? (
        <BiVideo className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ) : (
        <BiSolidBot className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242734_add_bot_message_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242877_add_bot_message_fr.mp3"
      },
      tooltipImage: isVideo
        ? "https://cdn.1cdn.app/application/LOUISEBOT/25012813145961_Video_Bot_Message-min.png"
        : "https://cdn.1cdn.app/application/LOUISEBOT/25012813125789_Bot_Message-min.png",
      description: isVideo
        ? t("VideoMacBotResponseForm/descrption")
        : t("MacBotResponseForm/descrption")
    },
    [MacBotActionKind.ShowUserInputForm]: {
      title: ReactHtmlParser(t("MacBotUserInputForm/title")),
      tooltip: t("MacBotUserInputForm/Tooltips"),
      youtube: "READ_USER_MESSAGE",
      icon: <BiUserVoice className="tw-w-6 tw-h-6 tw-relative" />,
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242782_read_user_message_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716242748_read_user_message_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813145815_Read_User_Messages-min.png",
      description: t("MacBotUserInputForm/description")
    },
    [MacBotActionKind.ShowVariableForm]: {
      title: ReactHtmlParser(t("MacBotCreateVariableForm/key10")),
      tooltip: t("MacBotVariableForm/Tooltips"),
      youtube: "ASSIGN_VARIABLE",
      icon: (
        <BiSolidShapes className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/2409271623042_assign_variable_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/2409271623041_assign_variable_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25012813125746_Assign_Variables-min.png",
      description: t("MacBotVariableForm/description")
    },
    [MacBotActionKind.ShowLINKForm]: {
      title: ReactHtmlParser(t("MacBotLinkForm/title")),
      tooltip: t("MacBotLinkForm/Tooltips"),
      youtube: "ADD_LINK",
      icon: (
        <BiLinkExternal className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />
      ),
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230449_add_clickable_link_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/24092716230438_add_clickable_link_fr.mp3"
      },
      tooltipImage: isVideo
        ? "https://cdn.1cdn.app/application/LOUISEBOT/25012813145955_Video_Button-min.png"
        : "https://cdn.1cdn.app/application/LOUISEBOT/25012813145790_External_Link-min.png",
      description: isVideo
        ? t("MacBotLinkForm/videoDescription")
        : t("MacBotLinkForm/description")
    },
    [MacBotActionKind.ShowElseForm]: {
      title: "",
      tooltip: "",
      youtube: "",
      icon: undefined,
      audioPath: {},
      tooltipImage: "",
      description: ""
    },
    [MacBotActionKind.ShowNotifyMeByEmail]: {
      title: ReactHtmlParser(t("MacNotifyEmail/key1")),
      tooltip: t("MacNotifyEmail/Tooltips"),
      icon: <BiAt className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800" />,
      description: t("MacNotifyEmail/key1"),
      youtube: "NOTIFY_ME_BY_EMAIL",
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/25021113384787_email_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/2502111338472_email_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25021112463364_notify_me_by_email-min.png"
    },
    [MacBotActionKind.CloseForm]: {
      title: "",
      tooltip: "",
      youtube: "",
      icon: undefined,
      audioPath: {},
      tooltipImage: "",
      description: ""
    },
    [MacBotActionKind.ShowReceiveAttachment]: {
      title: ReactHtmlParser(t("MacReceiveFile/key1")),
      tooltip: t("MacReceiveFile/Tooltips"),
      icon: (
        <BiFile
          size={22}
          className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800"
        />
      ),
      description: t("MacReceiveFile/description"),
      youtube: "RECEIVE_ATTACHMENT",
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/25021113415212_upload_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/2502111338472_upload_fr.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25021112463349_receive_file-min.png"
    },
    [MacBotActionKind.ShowPassToAi]: {
      title: ReactHtmlParser(t("PassToAI/Title")),
      tooltip: t("PassToAI/Tooltips"),
      icon: (
        <BiBrain
          size={22}
          className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800"
        />
      ),
      description: t("PassToAI/ToolDescription"),
      youtube: "PASS_TO_AI",
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/25050219374877_voice_passtoai_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/25050219324597_voice_passtoai.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041919053840_image_pass_to_AI.png"
    },
    [MacBotActionKind.ShowSwitchToChatbot]: {
      title: ReactHtmlParser(t("SwitchToChatbot/Title")),
      tooltip: t("SwitchToChatbot/Tooltips"),
      icon: (
        <BiFastForwardCircle
          size={22}
          className="tw-w-6 tw-h-6 tw-relative tw-text-gray-800"
        />
      ),
      description: t("SwitchToChatbot/ToolDescription"),
      youtube: "SWITCH_TO_CHATBOT",
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/2505021937487_voice_passtoother_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/25050219324565_voice_passtoother.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041919150785_switch_to_other_bot.png"
    },
    [MacBotActionKind.ShowSuperUserManagerServices]: {
      title: ReactHtmlParser(t("SuperUserManager/key1")),
      tooltip: t("SuperUserManager/connect/Tooltips"),
      description: t("SuperUserManager/connect/ToolDescription"),
      youtube: "SUPER_USER_MANAGER_SERVICES",
      icon: <SuperUserManagerIcon />,
      audioPath: {
        en: "https://cdn.1cdn.app/application/LOUISEBOT/25050219374899_voice_sum_en.mp3",
        fr: "https://cdn.1cdn.app/application/LOUISEBOT/25050219324571_voice_sum.mp3"
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041516381835_get_profile.png"
    },
    [MacBotActionKind.ShowCheckIfUserExists]: {
      title: ReactHtmlParser(t("SuperUserManager/checkUserExists")),
      tooltip: t("SuperUserManager/checkExist/Tooltips"),
      description: t("SuperUserManager/checkExist/ToolDescription"),
      youtube: "CHECK_IF_USER_EXISTS",
      icon: <SuperUserManagerIcon />,
      audioPath: {
        en: "",
        fr: ""
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041516381834_check_if_exist.png"
    },
    [MacBotActionKind.ShowUserCredential]: {
      title: ReactHtmlParser(t("SuperUserManager/userCredential")),
      tooltip: t("SuperUserManager/loginForm/Tooltips"),
      description: t("SuperUserManager/loginForm/ToolDescription"),
      youtube: "USER_CREDENTIAL",
      icon: <SuperUserManagerIcon />,
      audioPath: {
        en: "",
        fr: ""
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041516381898_login.png"
    },
    [MacBotActionKind.ShowGetUserInfo]: {
      title: ReactHtmlParser(t("SuperUserManager/getUserInfo")),
      tooltip: t("SuperUserManager/getInfo/Tooltips"),
      description: t("SuperUserManager/getInfo/ToolDescription"),
      youtube: "GET_USER_INFO",
      icon: <SuperUserManagerIcon />,
      audioPath: {
        en: "",
        fr: ""
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041516381835_get_profile.png"
    },
    [MacBotActionKind.ShowRegisterUser]: {
      title: ReactHtmlParser(t("SuperUserManager/key3")),
      tooltip: t("SuperUserManager/registerForm/Tooltips"),
      description: t("SuperUserManager/registerForm/ToolDescription"),
      youtube: "REGISTER_USER",
      icon: <SuperUserManagerIcon />,
      audioPath: {
        en: "",
        fr: ""
      },
      tooltipImage:
        "https://cdn.1cdn.app/application/LOUISEBOT/25041516381887_register.png"
    }
  };

  const {
    title,
    tooltip,
    youtube,
    icon,
    audioPath,
    tooltipImage,
    description
  } = HEADER_CONFIG[type] || {
    title: "Unknown",
    tooltip: "Unknown",
    icon: undefined
  };

  return (
    <div className="tw-h-14 tw-p-2 tw-justify-between tw-items-center tw-inline-flex tw-w-full">
      <div className="tw-p-2.5 tw-bg-gray-100 tw-rounded-full tw-justify-center tw-items-center tw-gap-2 tw-flex">
        {icon}
        <div className="tw-justify-start tw-items-center tw-gap-1 tw-flex">
          <div className="tw-text-gray-800 tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight">
            {title}
          </div>
          <PopoverWithAudio
            type="ADVANCED"
            data={{
              size: 20,
              contentText: tooltip,
              audioUrl: audioPath[i18n.language],
              popupImageUrl: tooltipImage,
              description: description
            }}
          />
        </div>
      </div>
      <div className="tw-justify-end tw-items-center tw-gap-2 tw-flex">
        <ScenarioYoutubeVideo
          item={{ name: youtube }}
          type={YoutubePosition.ScenarioHeader}
        />
        <button
          onClick={onClose}
          className="tw-p-2.5 tw-bg-white tw-rounded-full tw-shadow-sm tw-border tw-border-gray-300 tw-justify-start tw-items-center tw-gap-3 tw-flex tw-border-solid"
          type="button"
        >
          <BiX className="tw-w-6 tw-h-6 tw-relative tw-overflow-hidden" />
        </button>
      </div>
    </div>
  );
};

export default ScenarioHeader;
